import React, { useState } from 'react';
import { SlidersIcon, TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
interface InputParams {
  revenueTarget: number;
  segmentMix: {
    enterprise: number;
    midMarket: number;
    smb: number;
  };
  averageDealSize: {
    enterprise: number;
    midMarket: number;
    smb: number;
  };
  conversionRates: {
    sql_to_opportunity: number;
    opportunity_to_close: number;
  };
}
interface ScenarioModelingProps {
  params: InputParams;
}
export function ScenarioModeling({
  params
}: ScenarioModelingProps) {
  const [marketingSpend, setMarketingSpend] = useState(75);
  const [salesCapacity, setSalesCapacity] = useState(60);
  const [dealVelocity, setDealVelocity] = useState(50);
  const calculateImpact = () => {
    const baseRevenue = params.revenueTarget;
    const spendMultiplier = marketingSpend / 50 * 0.15;
    const capacityMultiplier = salesCapacity / 50 * 0.12;
    const velocityMultiplier = dealVelocity / 50 * 0.08;
    const totalImpact = spendMultiplier + capacityMultiplier + velocityMultiplier;
    const projectedRevenue = baseRevenue * (1 + totalImpact);
    const variance = (projectedRevenue - baseRevenue) / baseRevenue * 100;
    return {
      projectedRevenue,
      variance,
      pipelineGenerated: projectedRevenue * 3
    };
  };
  const impact = calculateImpact();
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  return <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3101">
      <div className="flex items-center gap-2 mb-6" data-id="element-3102">
        <SlidersIcon className="w-5 h-5 text-red-600" data-id="element-3103" />
        <h2 className="text-lg font-semibold text-gray-900" data-id="element-3104">
          Scenario Modeling & What-If Analysis
        </h2>
      </div>
      {/* Sliders */}
      <div className="space-y-6 mb-6" data-id="element-3105">
        <div data-id="element-3106">
          <div className="flex items-center justify-between mb-2" data-id="element-3107">
            <label className="text-sm font-medium text-gray-900" data-id="element-3108">
              Marketing Spend Increase
            </label>
            <span className="text-sm font-semibold text-red-600" data-id="element-3109">
              +{marketingSpend}%
            </span>
          </div>
          <input type="range" min="0" max="150" value={marketingSpend} onChange={e => setMarketingSpend(Number(e.target.value))} className="w-full" data-id="element-3110" />
          <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-3111">
            <span data-id="element-3112">0%</span>
            <span data-id="element-3113">75%</span>
            <span data-id="element-3114">150%</span>
          </div>
        </div>
        <div data-id="element-3115">
          <div className="flex items-center justify-between mb-2" data-id="element-3116">
            <label className="text-sm font-medium text-gray-900" data-id="element-3117">
              Sales Capacity Utilization
            </label>
            <span className="text-sm font-semibold text-red-600" data-id="element-3118">
              {salesCapacity}%
            </span>
          </div>
          <input type="range" min="0" max="100" value={salesCapacity} onChange={e => setSalesCapacity(Number(e.target.value))} className="w-full" data-id="element-3119" />
          <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-3120">
            <span data-id="element-3121">0%</span>
            <span data-id="element-3122">50%</span>
            <span data-id="element-3123">100%</span>
          </div>
        </div>
        <div data-id="element-3124">
          <div className="flex items-center justify-between mb-2" data-id="element-3125">
            <label className="text-sm font-medium text-gray-900" data-id="element-3126">
              Deal Velocity Improvement
            </label>
            <span className="text-sm font-semibold text-red-600" data-id="element-3127">
              +{dealVelocity}%
            </span>
          </div>
          <input type="range" min="0" max="100" value={dealVelocity} onChange={e => setDealVelocity(Number(e.target.value))} className="w-full" data-id="element-3128" />
          <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-3129">
            <span data-id="element-3130">0%</span>
            <span data-id="element-3131">50%</span>
            <span data-id="element-3132">100%</span>
          </div>
        </div>
      </div>
      {/* Impact Summary */}
      <div className="grid grid-cols-3 gap-4 p-4 bg-gradient-to-r from-red-50 to-purple-50 rounded-lg border border-red-200" data-id="element-3133">
        <div data-id="element-3134">
          <p className="text-xs text-gray-600 mb-1" data-id="element-3135">Projected Revenue</p>
          <p className="text-xl font-bold text-gray-900" data-id="element-3136">
            {formatCurrency(impact.projectedRevenue)}
          </p>
        </div>
        <div data-id="element-3137">
          <p className="text-xs text-gray-600 mb-1" data-id="element-3138">vs. Target</p>
          <div className="flex items-center gap-1" data-id="element-3139">
            {impact.variance >= 0 ? <TrendingUpIcon className="w-5 h-5 text-green-600" data-id="element-3140" /> : <TrendingDownIcon className="w-5 h-5 text-red-600" data-id="element-3141" />}
            <p className={`text-xl font-bold ${impact.variance >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-3142">
              {impact.variance >= 0 ? '+' : ''}
              {impact.variance.toFixed(1)}%
            </p>
          </div>
        </div>
        <div data-id="element-3143">
          <p className="text-xs text-gray-600 mb-1" data-id="element-3144">Pipeline Generated</p>
          <p className="text-xl font-bold text-gray-900" data-id="element-3145">
            {formatCurrency(impact.pipelineGenerated)}
          </p>
        </div>
      </div>
    </div>;
}